import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { socketManager } from 'utils/socketManager';
import {
  messageSocketConnecting,
  messageSocketConnected,
  messageSocketDisconnected,
  messageSocketError,
  messageSocketReconnectAttempt,
} from 'store/slices/messageSlice';
import type {
  NewMessagePayload,
  MessageReadPayload,
  NotificationCountsPayload,
} from 'types/interfaces/message';

export interface UseMessageSocketOptions {
  enabled?: boolean;
  onNewMessage?: (payload: NewMessagePayload) => void;
  onMessageRead?: (payload: MessageReadPayload) => void;
  onNotificationsUpdate?: (payload: NotificationCountsPayload) => void;
}

export interface UseMessageSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  emitMarkAsRead: (conversationId: string, messageIds: string[]) => void;
}

/**
 * Hook to manage WebSocket connection for real-time messaging.
 * Uses shared socket manager singleton.
 *
 * Events handled:
 * - message:new - New message received
 * - message:read - Messages marked as read (read receipt)
 * - notifications:update - Badge counts changed
 *
 * Events emitted:
 * - message:mark_read - Mark messages as read
 */
export const useMessageSocket = (
  options: UseMessageSocketOptions = {}
): UseMessageSocketReturn => {
  const { enabled = true, onNewMessage, onMessageRead, onNotificationsUpdate } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const { isConnected, isConnecting, connectionError } = useSelector(
    (state: RootState) => state.message
  );

  // Stable callback refs to avoid effect re-runs
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageReadRef = useRef(onMessageRead);
  const onNotificationsUpdateRef = useRef(onNotificationsUpdate);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onMessageReadRef.current = onMessageRead;
    onNotificationsUpdateRef.current = onNotificationsUpdate;
  }, [onNewMessage, onMessageRead, onNotificationsUpdate]);

  // Handle incoming message:new events
  const handleNewMessage = useCallback((data: NewMessagePayload) => {
    console.log('[MessageSocket] message:new received:', data);
    if (onNewMessageRef.current) {
      onNewMessageRef.current(data);
    }
  }, []);

  // Handle incoming message:read events (read receipts)
  const handleMessageRead = useCallback((data: MessageReadPayload) => {
    console.log('[MessageSocket] message:read received:', data);
    if (onMessageReadRef.current) {
      onMessageReadRef.current(data);
    }
  }, []);

  // Handle notifications:update events (badge count changes)
  const handleNotificationsUpdate = useCallback((data: NotificationCountsPayload) => {
    console.log('[MessageSocket] notifications:update received:', data);
    if (onNotificationsUpdateRef.current) {
      onNotificationsUpdateRef.current(data);
    }
  }, []);

  // Connection state handler
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        dispatch(messageSocketConnected());
      } else {
        dispatch(messageSocketDisconnected());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(messageSocketConnecting());
    hasAcquiredRef.current = true;

    const socket = socketManager.acquireSocket();

    // Subscribe to connection state changes
    socketManager.addConnectionListener(handleConnectionChange);

    // Check current connection state
    if (socket.connected) {
      dispatch(messageSocketConnected());
    }

    // Subscribe to socket events directly for immediate state updates
    const onConnect = () => {
      console.log('[MessageSocket] Connected');
      dispatch(messageSocketConnected());
    };

    const onDisconnect = (reason: string) => {
      console.log('[MessageSocket] Disconnected:', reason);
      dispatch(messageSocketDisconnected());
    };

    const onConnectError = (error: Error) => {
      console.error('[MessageSocket] Connection error:', error.message);
      dispatch(messageSocketError(error.message));
    };

    const onReconnectAttempt = (attemptNumber: number) => {
      console.log('[MessageSocket] Reconnect attempt:', attemptNumber);
      dispatch(messageSocketReconnectAttempt());
    };

    const onReconnectFailed = () => {
      console.error('[MessageSocket] Reconnection failed after max attempts');
      dispatch(messageSocketError('Reconnection failed'));
    };

    // Register connection event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);

    // Register message event handlers
    socket.on('message:new', handleNewMessage);
    socket.on('message:read', handleMessageRead);
    socket.on('notifications:update', handleNotificationsUpdate);

    // Cleanup on unmount
    return () => {
      console.log('[MessageSocket] Cleaning up');
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('message:new', handleNewMessage);
      socket.off('message:read', handleMessageRead);
      socket.off('notifications:update', handleNotificationsUpdate);
      socketManager.removeConnectionListener(handleConnectionChange);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [
    enabled,
    dispatch,
    handleNewMessage,
    handleMessageRead,
    handleNotificationsUpdate,
    handleConnectionChange,
  ]);

  // Emit mark as read event
  const emitMarkAsRead = useCallback(
    (conversationId: string, messageIds: string[]) => {
      const socket = socketManager.getSocket();
      if (socket?.connected) {
        console.log('[MessageSocket] Emitting message:mark_read', { conversationId, messageIds });
        socket.emit('message:mark_read', { conversationId, messageIds });
      }
    },
    []
  );

  return {
    isConnected,
    isConnecting,
    connectionError,
    emitMarkAsRead,
  };
};

export default useMessageSocket;
